import { useCallback, useEffect, useRef } from "react";

import type { EtagValidator } from "../primitives/useEtagValidator";
import type { PersistedSnapshot } from "./types";

/** The server's copy, normalised like the baseline, and its validator. */
export type ServerCopy = { snap: PersistedSnapshot; etag: string | null };
type ReadServer = (id: string) => Promise<ServerCopy | null>;

/** The open read, made once per id: useResumeConflict and useProgramVersion
 *  share it, so opening the editor costs one GET. */
export function useOpenRead(readServer: ReadServer | null) {
  const opened = useRef<{ id: string; copy: Promise<ServerCopy | null> } | null>(null);
  return useCallback(
    (id: string): Promise<ServerCopy | null> => {
      if (readServer === null) return Promise.resolve(null);
      if (opened.current?.id !== id) opened.current = { id, copy: readServer(id) };
      return opened.current.copy;
    },
    [readServer],
  );
}

// S5 (#336): the program's version, and «Modificato altrove».
//  - At open, the server's validator is armed whatever its copy holds. A copy
//    the editor does not show replaces it when nothing was edited yet, and
//    otherwise asks the resume question (`conflict`): no PATCH is spent to
//    learn what the read already said.
//  - `verify`: a save with no validator in hand (a queued save moved the
//    version unread) reads it first. Same content as the baseline: armed, go.
//    Different: «Modificato altrove». Unreadable: go unlocked, as the queue
//    goes (Q3).
//  - The dialog stays up while its answer reads the server, and closes in the
//    same tick the save turns busy: Save is never live in between.
export function useProgramVersion({
  lockId,
  readOnce,
  readServer,
  etag,
  sameContent,
  getBaseline,
  edited,
  conflict,
  savedAt,
  resetTo,
  markSaved,
  savedElsewhere,
  closeSavedElsewhere,
  save,
}: {
  /** The existing program opened, or null: nothing to lock. Read once. */
  lockId: string | null;
  /** The shared open read (useOpenRead). */
  readOnce: ReadServer;
  /** A fresh GET of the server's copy; null when there is none readable. */
  readServer: ReadServer | null;
  etag: EtagValidator;
  sameContent: (a: PersistedSnapshot, b: PersistedSnapshot) => boolean;
  getBaseline: () => PersistedSnapshot | undefined;
  /** The editor's value differs from its baseline. */
  edited: boolean;
  /** The resume question (useResumeConflict): asking, and how to ask. */
  conflict: { pending: boolean; show: (server: PersistedSnapshot) => void };
  /** When this editor last wrote; once it has, the open read is stale. */
  savedAt: number | undefined;
  resetTo: (value: PersistedSnapshot) => void;
  markSaved: (baseline: PersistedSnapshot) => void;
  savedElsewhere: { id: string } | null;
  closeSavedElsewhere: (note?: { failed: unknown }) => void;
  save: () => Promise<boolean>;
}) {
  const wrote = useRef(false);
  useEffect(() => {
    if (savedAt !== undefined) wrote.current = true;
  }, [savedAt]);
  // Read when the open read lands, not when it left.
  const editedRef = useRef(edited);
  editedRef.current = edited;
  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;

  useEffect(() => {
    if (lockId === null) return;
    let live = true;
    readOnce(lockId)
      .then((copy) => {
        // A save since carries its own, newer validator.
        if (!live || copy === null || wrote.current) return;
        etag.onEtag(copy.etag);
        const baseline = getBaseline();
        if (baseline === undefined || sameContent(baseline, copy.snap)) return;
        const ask = conflictRef.current;
        if (ask.pending || editedRef.current) ask.show(copy.snap);
        else {
          resetTo(copy.snap);
          markSaved(copy.snap);
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // Once per mount, like the resume read it shares.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // «Tieni» with the server unreadable: the overwrite goes unlocked, and
  // `verify` must not stop it.
  const unlocked = useRef(false);
  const verify = useCallback(
    async (id: string): Promise<boolean> => {
      if (unlocked.current || readServer === null) return true;
      const copy = await readServer(id).catch(() => null);
      if (copy === null) return true;
      const baseline = getBaseline();
      if (baseline === undefined || !sameContent(baseline, copy.snap)) return false;
      etag.onEtag(copy.etag);
      return true;
    },
    [readServer, getBaseline, sameContent, etag],
  );

  // One answer at a time: a second tap while the first reads does nothing.
  const working = useRef(false);
  const keepMine = useCallback(async () => {
    if (savedElsewhere === null || working.current) return;
    working.current = true;
    try {
      const copy =
        readServer === null
          ? null
          : await readServer(savedElsewhere.id).catch(() => null);
      // Unreadable or no reader: the overwrite is still the answer, unlocked.
      if (copy?.etag) etag.onEtag(copy.etag);
      else {
        etag.clear();
        unlocked.current = true;
      }
      closeSavedElsewhere();
      await save();
    } finally {
      unlocked.current = false;
      working.current = false;
    }
  }, [savedElsewhere, readServer, closeSavedElsewhere, etag, save]);

  const takeServer = useCallback(async () => {
    if (savedElsewhere === null || working.current) return;
    working.current = true;
    try {
      const copy = readServer === null ? null : await readServer(savedElsewhere.id);
      if (copy === null) throw new Error("server_copy_unreadable");
      resetTo(copy.snap);
      markSaved(copy.snap);
      etag.onEtag(copy.etag);
      closeSavedElsewhere();
    } catch (err) {
      // The draft stays; the error line says nothing was saved.
      closeSavedElsewhere({ failed: err });
    } finally {
      working.current = false;
    }
  }, [savedElsewhere, readServer, closeSavedElsewhere, resetTo, markSaved, etag]);

  return {
    verify,
    dialog: {
      visible: savedElsewhere !== null,
      onKeep: () => {
        keepMine().catch(() => undefined);
      },
      onDrop: () => {
        takeServer().catch(() => undefined);
      },
      // Back: nothing written, the old validator stays.
      onClose: () => closeSavedElsewhere(),
    },
  };
}
